import { useEffect, useRef, useState } from 'react';
import type { Meta } from '@streamit/contracts';
import { channelProgrammes, guideRefreshDelay, localGuideDay, localGuideWindow, type Programme } from '../../../packages/contracts/src/live';
import { metadataHttps } from '../../../packages/contracts/src/metadata';
import { useGuideRefresh } from './guide-refresh';
import './live-guide.css';

function useGuideClock(active = true) {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    if (!active) return;
    const update = () => { if (!document.hidden) setNow(Date.now()); };
    update(); const timer = window.setInterval(update, 30_000);
    document.addEventListener('visibilitychange', update);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', update); };
  }, [active]);
  return now;
}
const time = (at: number) => new Date(at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZoneName: 'short', hour12: false });
const zone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;
const status = { past: 'Ended', now: 'On now', upcoming: 'Upcoming' };

export function GuideChannels({ items, day, onSelect }: { items: Meta[]; day: string; onSelect(meta: Meta): void }) {
  const now = useGuideClock(), window = localGuideWindow(day);
  return <section className="live-guide" aria-label="Channel guide"><h2>Channel guide · {day} · {zone()}</h2>
    <ul>{items.map(meta => {
      const rows = window ? channelProgrammes(meta, window, now) : [], current = rows.find(p => p.status === 'now');
      const preview = current ? rows.slice(rows.indexOf(current), rows.indexOf(current) + 3) : rows.filter(p => p.status === 'upcoming').slice(0, 3);
      const shown = preview.length ? preview : rows.slice(-3);
      return <li key={JSON.stringify([meta.type, meta.id])}>
        <button onClick={() => onSelect(meta)} aria-label={`Open channel ${meta.name}`}><strong>{meta.name}</strong></button>
        {!current && <p>{rows.length ? 'No programme is on air in this schedule.' : 'No programme schedule is available for this date.'}</p>}
        {shown.map(p => <p key={p.video.id}><span className="live-guide-status">{status[p.status]}</span> {time(p.start)}–{time(p.end)} · {p.video.title || 'Untitled programme'}</p>)}
        {!!rows.length && <details><summary>Full schedule for {meta.name}</summary><ProgrammeList key={day} programmes={rows}/></details>}
      </li>;
    })}</ul>
  </section>;
}

export function LiveSchedule({ meta, onWatch, onRefresh, onTrailers, active = true }: {
  meta: Meta; onWatch(): void; onRefresh?(signal?: AbortSignal): Promise<void>; onTrailers?(video: string): void; active?: boolean;
}) {
  const now = useGuideClock(active), day = localGuideDay(now), window = localGuideWindow(day);
  const [failed, setFailed] = useState(false), [refreshing, setRefreshing] = useState(false);
  const owner = JSON.stringify([meta.type, meta.id]);
  const pending = useRef<{ controller: AbortController; done: Promise<void> } | null>(null);
  useEffect(() => { setFailed(false); }, [owner]);
  useEffect(() => {
    setRefreshing(false);
    return () => {
      const request = pending.current; pending.current = null;
      request?.controller.abort();
    };
  }, [owner, active, !!onRefresh]);
  const programmes = window ? channelProgrammes(meta, window, now) : [];
  const refresh = async (signal?: AbortSignal) => {
    if (!active || !onRefresh || signal?.aborted) return;
    let request = pending.current;
    if (!request) {
      request = { controller: new AbortController(), done: Promise.resolve() };
      pending.current = request; setRefreshing(true);
      const current = request;
      current.done = (async () => {
        try { await onRefresh(current.controller.signal);
          if (pending.current === current && !current.controller.signal.aborted) setFailed(false);
        } catch {
          if (pending.current === current && !current.controller.signal.aborted) setFailed(true);
        } finally {
          if (pending.current === current) { pending.current = null; setRefreshing(false); }
        }
      })();
    }
    // Timer ticks join a manual retry, and both use the hook's cancellation.
    const current = request, abort = () => current.controller.abort();
    signal?.addEventListener('abort', abort, { once: true });
    try { await current.done; }
    finally { signal?.removeEventListener('abort', abort); }
  };
  useGuideRefresh(owner, active && !!onRefresh, guideRefreshDelay(meta, now, failed), refresh);
  return <section className="live-guide" aria-label="Live channel schedule">
    <div className="live-guide-actions"><h2>Live channel · {meta.name}</h2><button onClick={onWatch}>Watch channel</button>
      {onRefresh && <button disabled={!active || refreshing} aria-busy={refreshing} onClick={() => { void refresh(); }}>{refreshing ? 'Refreshing guide…' : 'Refresh guide'}</button>}</div>
    <p>{day} · Programme times are in {zone()}. Watching always opens the live channel.</p>
    {failed && <p role="alert">Guide refresh failed. The previous schedule is shown; another refresh will be tried while this page is open.</p>}
    {!programmes.some(p => p.status === 'now') && <p>No programme is on air in this schedule.</p>}
    {!programmes.length && <p>The addon has no schedule for this date. You can still choose a channel source.</p>}
    <ProgrammeList key={day} programmes={programmes} onTrailers={onTrailers}/>
  </section>;
}

function ProgrammeList({ programmes, onTrailers }: { programmes: Programme[]; onTrailers?(video: string): void }) {
  const [page, setPage] = useState(0);
  const first = useRef<HTMLLIElement>(null), focusPage = useRef(false);
  const last = Math.max(0, Math.ceil(programmes.length / 24) - 1), current = Math.min(page, last);
  const shown = programmes.slice(current * 24, (current + 1) * 24);
  useEffect(() => {
    if (focusPage.current) { first.current?.focus(); first.current?.scrollIntoView({ block: 'start' }); focusPage.current = false; }
  }, [current]);
  const move = (next: number) => { focusPage.current = true; setPage(next); };
  return <>
    <ul>{shown.map((p, i) => <li key={p.video.id} ref={i === 0 ? first : undefined} tabIndex={0} aria-label={`Programme: ${p.video.title || 'Untitled programme'}`}>
      <span className="live-guide-status">{status[p.status]}</span><time dateTime={p.video.startTime}>{time(p.start)}–{time(p.end)}</time>
      <h3>{p.video.title || 'Untitled programme'}</h3>
      {metadataHttps(p.video.thumbnail) && <img src={metadataHttps(p.video.thumbnail)} alt="" loading="lazy" referrerPolicy="no-referrer"/>}
      {p.video.overview && <p>{p.video.overview}</p>}
      <p>{[p.video.runtime, p.video.releaseInfo, p.video.genres?.join(', '), p.video.ratings?.map(r => [r.system, r.value].filter(Boolean).join(' ')).join(' · ')].filter(Boolean).join(' · ')}</p>
      {!!p.video.cast?.length && <p>Cast: {p.video.cast.join(', ')}</p>}{!!p.video.directors?.length && <p>Director: {p.video.directors.join(', ')}</p>}
      {p.video.embeddedTrailers && onTrailers && <button aria-label={`Trailers: ${p.video.title || p.video.id}`} onClick={() => onTrailers(p.video.id)}>Trailers</button>}
    </li>)}</ul>
    {last > 0 && <div className="live-guide-actions"><button disabled={!current} onClick={() => move(current - 1)}>Earlier programmes</button>
      <span>{current * 24 + 1}–{current * 24 + shown.length} of {programmes.length}</span>
      <button disabled={current >= last} onClick={() => move(current + 1)}>More programmes</button></div>}
  </>;
}
